/**
 * Subtle weather widget data · fetched from Open-Meteo (free, no API
 * key, fair-use limits well above what a single intranet needs).
 *
 * Defaults to Brussels (close enough for the LampTwist offices in
 * Tournai and Uccle). The result is cached in module-level memory for
 * 15 minutes so navigating between pages doesn't re-fetch on every
 * mount.
 */

const DEFAULT_LAT = 50.85;   // Brussels
const DEFAULT_LON = 4.35;
const TTL_MS = 15 * 60 * 1000;

let _cache = null;  // { at: number, data: {...} }
let _inflight = null;

export async function fetchWeather({ lat = DEFAULT_LAT, lon = DEFAULT_LON } = {}) {
  const now = Date.now();
  if (_cache && now - _cache.at < TTL_MS) return _cache.data;
  if (_inflight) return _inflight;

  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`;
  _inflight = fetch(url)
    .then(r => r.ok ? r.json() : Promise.reject(new Error('weather: ' + r.status)))
    .then(json => {
      const cw = json.current_weather || {};
      const data = {
        temperature: Math.round(cw.temperature),  // °C
        code: cw.weathercode,
        windSpeed: cw.windspeed,
        time: cw.time,
      };
      _cache = { at: Date.now(), data };
      _inflight = null;
      return data;
    })
    .catch(err => {
      _inflight = null;
      throw err;
    });
  return _inflight;
}

/* Map a WMO weather code to a lucide-react icon name + a short label.
 * Conservative palette · only a handful of icons so the topbar stays calm. */
export function describeCode(code) {
  if (code === 0) return { icon: 'Sun', label: 'clear' };
  if (code <= 3)  return { icon: 'CloudSun', label: 'partly cloudy' };
  if (code === 45 || code === 48) return { icon: 'CloudFog', label: 'fog' };
  if (code >= 51 && code <= 57) return { icon: 'CloudDrizzle', label: 'drizzle' };
  if (code >= 61 && code <= 67) return { icon: 'CloudRain', label: 'rain' };
  if (code >= 71 && code <= 77) return { icon: 'CloudSnow', label: 'snow' };
  if (code >= 80 && code <= 82) return { icon: 'CloudRain', label: 'showers' };
  if (code >= 85 && code <= 86) return { icon: 'CloudSnow', label: 'snow showers' };
  if (code >= 95) return { icon: 'CloudLightning', label: 'thunderstorm' };
  return { icon: 'Cloud', label: 'cloudy' };
}
